import type {
    DiaryDocType,
    MediaDocType,
    MediaLinkDocType,
    MediaGroupDocType,
    ParcelDocType,
    ProjectDocType
} from '@ast-data/shared';
import type { RxCollection } from 'rxdb';
import { composeFileName, fileNameSpan } from '../../../utils/downloadFile';
import { toDayString } from '../../../utils/formatDate';
import {
    byMeasureThen,
    measureFileNamePart,
    projectsById,
    type ExportScope
} from '../pdf/exportScope';
import {
    findPlacedMedia,
    resolveExportMedia,
    type ExportMedia
} from '../pdf/exportMedia';

import { compareNatural } from '../../../utils/compare';
import { showDocs } from '../../users/personLabels';
import {
    createDeliverableTexts,
    type DeliverableTexts
} from '../../translation/deliverableTexts';

/** The collections the Tagebuch export reads. */
export interface DiaryExportCollections {
    projects: RxCollection<ProjectDocType>;
    diaries: RxCollection<DiaryDocType>;
    parcels: RxCollection<ParcelDocType>;
    media: RxCollection<MediaDocType>;
    mediaLinks: RxCollection<MediaLinkDocType>;
    mediaGroups: RxCollection<MediaGroupDocType>;
}

/** One Tagebuch day, with everything the sheet prints resolved. */
export interface DiaryExportDay {
    diary: DiaryDocType;
    /**
     * The Maßnahme THIS day belongs to — per sheet, not per export, so a
     * cross-project selection prints as one document whose sheets each say
     * where they are from.
     */
    project: ProjectDocType;
    /** The day's Flurstücke, each as its full Landkreis-to-number line. */
    parcels: string[];
    /** Sketches + photos of the day, own and linked, createdAt-ascending. */
    media: ExportMedia[];
}

export interface DiaryExportData {
    days: DiaryExportDay[];
}

/**
 * The shared export grammar ({@link composeFileName}): the Maßnahme, the
 * subject, then WHICH days are in it ({@link fileNameSpan}) —
 *
 * - one day    → "M-2024-0123-1_Tagebuch_2026-08-05.pdf"
 * - two days   → "M-2024-0123-1_Tagebuch_2026-08-05_2026-08-07.pdf"
 * - three plus → "M-2024-0123-1_Tagebuch_2026-04-01_2026-09-30_112Tage.pdf"
 *
 * Dates stay in the sortable 'YYYY-MM-DD' form rather than the German one the
 * app prints: in a file name they are read by a folder listing first.
 *
 * `recordedAt` are the days' capture timestamps in print order, ISO-UTC, so
 * each is reduced to its LOCAL calendar day — the one the sheet is headed by.
 * `measureNumbers` is one per day; a document of several Maßnahmen is named by
 * how many rather than by the first (see measureFileNamePart).
 */
export function diaryPdfFileName(
    measureNumbers: readonly string[],
    recordedAt: readonly string[]
): string {
    return composeFileName(
        [
            measureFileNamePart(measureNumbers),
            'Tagebuch',
            ...fileNameSpan(recordedAt.map(toDayString), 'Tage')
        ],
        '.pdf'
    );
}

/** The administrative line: Landkreis, Gemeinde, Gemarkung. */
function parcelArea(parcel: ParcelDocType): string[] {
    return [
        parcel.district,
        parcel.municipality,
        parcel.cadastralDistrict
    ].filter(Boolean);
}

/**
 * A day's parcels as the rows the sheet prints — one row per administrative
 * line, its Flurstücksnummern enumerated on it, so three Flurstücke of one
 * Gemarkung do not read as three places.
 *
 * The line is ordered rather than labelled: spelling the parts out ("Lkr. …,
 * Gde. …, Gmkg. …, Flst. …") costs ~95pt of the 266 the header row has, which
 * pushed the Flurstücksnummer onto a second line. It is the same " · " chain
 * the parcel pickers use.
 *
 * Grouped on the WHOLE line, never on the Gemarkung name alone: 532 Bavarian
 * Gemarkungen straddle several Gemeinden, so two different places would
 * otherwise collapse into one row.
 *
 * Rows and numbers are sorted and deduplicated because `parcelIds` is a
 * set-union merge field: its order differs from device to device, and printing
 * it as stored would make the same day's sheet depend on which tablet exported
 * it.
 */
export function parcelRows(parcels: ParcelDocType[]): string[] {
    const byArea = new Map<string, { area: string[]; numbers: Set<string> }>();
    for (const parcel of parcels) {
        const area = parcelArea(parcel);
        // NUL as the separator: no Gemarkung or Flur can contain it, so two
        // different areas can never collide into one key.
        const key = area.join('\u0000');
        const row = byArea.get(key) ?? { area, numbers: new Set<string>() };
        if (parcel.parcelNumber) {
            row.numbers.add(parcel.parcelNumber);
        }
        byArea.set(key, row);
    }
    return [...byArea.values()]
        .map(({ area, numbers }) =>
            [
                ...area,
                [...numbers]
                    .sort((left, right) => compareNatural(left, right))
                    .join(', ')
            ]
                .filter(Boolean)
                .join(' · ')
        )
        .sort((left, right) => compareNatural(left, right));
}

/**
 * Read Tagebuch days into the plain data the react-pdf document renders from,
 * chronologically and grouped by Maßnahme first, so a cross-project document
 * reads as one chapter per Maßnahme.
 *
 * The `project` scope is the project-level export (the Projekte grid's row
 * menu); the grids pass ticked ids instead, and those may span Maßnahmen.
 *
 * Days whose Maßnahme is not in the local
 * database are DROPPED — the sheet's whole head comes from the project.
 */
export async function collectDiaryExportData({
    collections,
    scope,
    labelOf,
    deliverable = createDeliverableTexts(null, null)
}: {
    collections: DiaryExportCollections;
    scope: ExportScope;
    /** Shows linked people by their accounts — see usePersonLabels. */
    labelOf?: (id: string) => string;
    /** Puts the accepted German translations in; without, the originals. */
    deliverable?: DeliverableTexts;
}): Promise<DiaryExportData> {
    const selector =
        scope.kind === 'project'
            ? { projectId: scope.projectId }
            : { id: { $in: [...scope.ids] } };
    const diaryDocs = await collections.diaries.find({ selector }).exec();
    const diaries = await deliverable.apply(
        'diary',
        showDocs<DiaryDocType>('diaries', diaryDocs, labelOf)
    );
    if (diaries.length === 0) {
        return { days: [] };
    }

    const projectDocs = await collections.projects.find().exec();
    const byId = projectsById(
        showDocs<ProjectDocType>('projects', projectDocs, labelOf)
    );
    const printable = diaries
        .filter((diary) => byId.has(diary.projectId))
        .sort(
            byMeasureThen(
                (diary) => byId.get(diary.projectId)?.measureNumber ?? '',
                (firstDiary, secondDiary) =>
                    firstDiary.recordedAt.localeCompare(secondDiary.recordedAt)
            )
        );

    // One lookup for every parcel any of the days references — a season keeps
    // coming back to the same handful of Flurstücke.
    const parcelIds = [
        ...new Set(printable.flatMap((diary) => diary.parcelIds ?? []))
    ];
    const parcelById = new Map<string, ParcelDocType>();
    if (parcelIds.length > 0) {
        const parcelDocs = await collections.parcels
            .find({ selector: { id: { $in: parcelIds } } })
            .exec();
        for (const doc of parcelDocs) {
            parcelById.set(doc.id, doc.toJSON() as ParcelDocType);
        }
    }

    const mediaFor = await resolveExportMedia(
        await findPlacedMedia(
            collections.media,
            collections.mediaLinks,
            collections.mediaGroups,
            printable.map((diary) => diary.id)
        )
    );

    return {
        days: printable.map((diary) => ({
            diary,
            // Present — `printable` dropped every day whose project is not.
            project: byId.get(diary.projectId) as ProjectDocType,
            parcels: parcelRows(
                (diary.parcelIds ?? [])
                    .map((id) => parcelById.get(id))
                    .filter((parcel): parcel is ParcelDocType => !!parcel)
            ),
            media: mediaFor(diary.id)
        }))
    };
}
